/* eslint-disable prettier/prettier */

import React, { useEffect, useMemo, useState } from 'react';

import styles from '../../../../styles/components/pageComponents/coach/CoachingTable.module.css';

import { useCoachingHours } from '../../../../hooks/useCoachingHours';

import SuccessContent from '../../../controlVariations/ModularSuccessModal';

import ErrorPopupModal from '../../../controlVariations/ErrorPopupMoadal';

import Tooltip from '../../../../components/common/ToolTip';

import ResponsiveModal from '../../../controlVariations/ResponsiveModal';
import ConfirmDownloadModal from '../../../controlVariations/ConfirmDownloadModal';
import { useAzureFileDownload } from '../../../../hooks/useAzureFileDownload';
import { getApiErrorMessage } from '../../../../utils/getApiErrorMessage';

//
// -------------------------------------------
// TYPES
// -------------------------------------------
//

export interface CoachingRecord {
	id: string;

	learnerName: string;

	hours: string;

	date: string;

	recording: string;

	recordingFileId?: string | null;

	approved: boolean;
}

//
// -------------------------------------------
// PROPS
// -------------------------------------------
//

interface CoachingTableProps {
	records: CoachingRecord[];

	loading?: boolean;

	error?: string | null;

	onReview: (
		id: string,
		approved: boolean
	) => Promise<{
		success: boolean;
		message?: string;
	}>;
}

//
// -------------------------------------------
// SVGs
// -------------------------------------------
//

const EditIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="40"
		height="40"
		viewBox="0 0 40 40"
		fill="none"
	>
		<rect
			x="0.5"
			y="0.5"
			width="39"
			height="39"
			rx="4.5"
			fill="white"
		/>
		<rect
			x="0.5"
			y="0.5"
			width="39"
			height="39"
			rx="4.5"
			stroke="#D9D9D9"
		/>
		<path
			d="M22.9999 13.0004L26.9999 17.0004M29.1739 14.8124C29.7026 14.2838 29.9997 13.5668 29.9998 12.8192C29.9999 12.0716 29.703 11.3546 29.1744 10.8259C28.6459 10.2972 27.9289 10.0001 27.1813 10C26.4337 9.99991 25.7166 10.2968 25.1879 10.8254L11.8419 24.1744C11.6098 24.4059 11.438 24.6909 11.3419 25.0044L10.0209 29.3564C9.99509 29.4429 9.99314 29.5347 10.0153 29.6222C10.0374 29.7097 10.0829 29.7896 10.1467 29.8534C10.2106 29.9172 10.2906 29.9624 10.3781 29.9845C10.4656 30.0065 10.5575 30.0044 10.6439 29.9784L14.9969 28.6584C15.3101 28.5631 15.5951 28.3925 15.8269 28.1614L29.1739 14.8124Z"
			stroke="#1E1E1E"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const SaveIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="40"
		height="40"
		viewBox="0 0 40 40"
		fill="none"
	>
		<rect
			x="0.5"
			y="0.5"
			width="39"
			height="39"
			rx="4.5"
			fill="white"
		/>
		<rect
			x="0.5"
			y="0.5"
			width="39"
			height="39"
			rx="4.5"
			stroke="#D9D9D9"
		/>
		<path
			d="M25.5556 30V22.2222C25.5556 21.9275 25.4385 21.6449 25.2301 21.4365C25.0217 21.2282 24.7391 21.1111 24.4444 21.1111H15.5556C15.2609 21.1111 14.9783 21.2282 14.7699 21.4365C14.5615 21.6449 14.4444 21.9275 14.4444 22.2222V30M14.4444 10V14.4444C14.4444 14.7391 14.5615 15.0217 14.7699 15.2301C14.9783 15.4385 15.2609 15.5556 15.5556 15.5556H23.3333M23.5556 10C24.1417 10.0083 24.7008 10.248 25.1111 10.6667L29.3333 14.8889C29.752 15.2992 29.9917 15.8583 30 16.4444V27.7778C30 28.3671 29.7659 28.9324 29.3491 29.3491C28.9324 29.7659 28.3671 30 27.7778 30H12.2222C11.6329 30 11.0676 29.7659 10.6509 29.3491C10.2341 28.9324 10 28.3671 10 27.7778V12.2222C10 11.6329 10.2341 11.0676 10.6509 10.6509C11.0676 10.2341 11.6329 10 12.2222 10H23.5556Z"
			stroke="#1E1E1E"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

//
// -------------------------------------------
// COMPONENT
// -------------------------------------------
//

const CoachingTable: React.FC<CoachingTableProps> = ({
	records,
	loading,
	error,
	onReview,
}) => {
	//
	// -------------------------------------------
	// LOCAL STATES
	// -------------------------------------------
	//

	const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

	const [localRecords, setLocalRecords] = useState(records);

	useEffect(() => {
		setLocalRecords(records);
	}, [records]);

	//
	// Popup States
	//
	const [showSuccessModal, setShowSuccessModal] = useState(false);

	const [showErrorModal, setShowErrorModal] = useState(false);

	const [errorMessage, setErrorMessage] = useState('');

	const [showDownloadConfirm, setShowDownloadConfirm] =
		useState(false);

	const [selectedRecording, setSelectedRecording] =
		useState<CoachingRecord | null>(null);

	const {
		downloadFile,
		downloading,
	} = useAzureFileDownload();

	//
	// -------------------------------------------
	// EDIT
	// -------------------------------------------
	//

	const handleEditClick = (id: string) => {
		setEditingRows(prev => ({
			...prev,
			[id]: true,
		}));
	};

	const handleRecordingClick = (record: CoachingRecord) => {
		if (!record.recordingFileId) {
			setErrorMessage('Recording file is not available for download.');
			setShowErrorModal(true);
			return;
		}

		setSelectedRecording(record);
		setShowDownloadConfirm(true);
	};

	//
	// -------------------------------------------
	// SAVE
	// -------------------------------------------
	//

	const handleSaveClick = async (id: string) => {
		try {
			const record = localRecords.find(r => r.id === id);

			if (!record) {
				return;
			}

			const result = await onReview(id, record.approved);

			if (!result.success) {
				setErrorMessage(
					result.message || 'Failed to update coaching hours'
				);

				setShowErrorModal(true);

				return;
			}

			setShowSuccessModal(true);

			setTimeout(() => {
				setShowSuccessModal(false);
			}, 3000);

			setEditingRows(prev => ({
				...prev,
				[id]: false,
			}));
		} catch (err) {
			setErrorMessage('Failed to update coaching hours');

			setShowErrorModal(true);
		}
	};

	//
	// -------------------------------------------
	// CHECKBOX CHANGE
	// -------------------------------------------
	//

	const handleCheckboxChange = (id: string, isChecked: boolean) => {
		setLocalRecords(prevRecords =>
			localRecords.map(record =>
				record.id === id
					? {
						...record,
						approved: isChecked,
					}
					: record
			)
		);
	};

	//
	// -------------------------------------------
	// JSX
	// -------------------------------------------
	//

	return (

		<>
			<div className={styles.cardContainer}>
				<h2 className={styles.title}>Coaching Hours</h2>

				{/* Loading */}
				{loading && (
					<div className={styles.loadingState}>
						Loading coaching hours...
					</div>
				)}

				{/* Error */}
				{error && <div className={styles.errorState}>{error}</div>}

				{/* Empty State */}
				{!loading && !error && localRecords.length === 0 && (
					<div className={styles.emptyState}>
						No coaching logs available.
					</div>
				)}

				{/* Table */}
				{!loading && !error && localRecords.length > 0 && (
					<div className={styles.tableScrollWrapper}>
						<table className={styles.table}></table>
					</div>
				)}
				<div className={styles.tableScrollWrapper}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Learner Name</th>

								<th>Number of Hours</th>

								<th>Date</th>

								<th>Recording/Transcript</th>

								<th className={styles.centerAlign}>Approved</th>

								<th className={styles.centerAlign}>Actions</th>
							</tr>
						</thead>

						<tbody>
							{localRecords.map(record => {
								const isEditing = editingRows[record.id];

								return (
									<tr key={record.id}>
										<td>{record.learnerName}</td>

										<td className={styles.lightText}>
											{record.hours}
										</td>

										<td className={styles.lightText}>
											{record.date}
										</td>
										<td className={styles.lightText}>
											{record.recordingFileId ? (
												<button
													type="button"
													onClick={() => handleRecordingClick(record)}
													disabled={downloading}
													style={{
														background: 'none',
														border: 'none',
														padding: 0,
														margin: 0,
														cursor: downloading ? 'default' : 'pointer',
														color: '#147B58',
														textDecoration: 'underline',
														textUnderlineOffset: '3px',
														font: 'inherit',
														textAlign: 'left',
													}}
													title="Click to download recording/transcript"
												>
													{record.recording}
												</button>
											) : (
												<span>{record.recording}</span>
											)}
										</td>

										{/* Checkbox */}
										<td className={styles.centerAlign}>
											<input
												type="checkbox"
												className={styles.checkbox}
												checked={record.approved}
												disabled={!isEditing}
												onChange={e =>
													handleCheckboxChange(
														record.id,
														e.target.checked
													)
												}
											/>
										</td>

										{/* Actions */}
										<td className={styles.centerAlign}>
											{isEditing ? (
												<button
													className={styles.actionButton}
													onClick={() =>
														handleSaveClick(record.id)
													}
												>
													<Tooltip
														text="Save Item"
													>
														<SaveIcon />
													</Tooltip>
												</button>
											) : (
												<button
													className={styles.actionButton}
													onClick={() =>
														handleEditClick(record.id)
													}
												>
													<Tooltip
														text="Edit Item"
													>
														<EditIcon />
													</Tooltip>
												</button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				{/* ---------------------------------- */}
				{/* SUCCESS MODAL */}
				{/* ---------------------------------- */}

				{showSuccessModal && (
					<SuccessContent
						title="Review Updated"
						subtitle="Coaching hours review updated successfully."
					>
					</SuccessContent>
				)}

				{/* ---------------------------------- */}
				{/* ERROR MODAL */}
				{/* ---------------------------------- */}


			</div>

			{showErrorModal && (
				<div className={styles.overlay}>
					<ErrorPopupModal
						title={errorMessage}
						onCancel={() => setShowErrorModal(false)}
					/>
				</div>
			)}

			<ResponsiveModal
				open={showDownloadConfirm}
				onClose={() => {
					setShowDownloadConfirm(false);
					setSelectedRecording(null);
				}}
			>
				<ConfirmDownloadModal
					onConfirm={async () => {
						if (!selectedRecording?.recordingFileId) {
							return;
						}

						const fileId = selectedRecording.recordingFileId;

						// ✅ Hide confirmation immediately
						setShowDownloadConfirm(false);
						setSelectedRecording(null);

						try {
							const result = await downloadFile(fileId);

							if (!result) {
								return;
							}

							const link = document.createElement('a');

							link.href = result.file;
							link.download = result.filename;

							document.body.appendChild(link);
							link.click();
							document.body.removeChild(link);
						} catch (err) {
							console.error(
								'❌ Coaching recording download failed:',
								err
							);

							setErrorMessage(
								getApiErrorMessage(err)
							);

							setShowErrorModal(true);
						}
					}}
					onCancel={() => {
						setShowDownloadConfirm(false);
						setSelectedRecording(null);
					}}
				/>
			</ResponsiveModal>
		</>
	);
};

export default CoachingTable;
